import type { AssignmentPlan, LiveParticipantOption, ManualPlacementDraft, RoomParticipant } from "../types.js";

export interface LateEmailMatch {
  live: LiveParticipantOption;
  participant: RoomParticipant;
  roomName: string;
  roomKey: string;
  attendeeKey: string;
}

export type LateEmailResolution =
  | { kind: "auto"; match: LateEmailMatch }
  | { kind: "confirmed"; match: LateEmailMatch; message: string }
  | { kind: "conflict"; match: LateEmailMatch; message: string }
  | { kind: "already-assigned" }
  | { kind: "none"; reason: string };

export interface LateEmailResolutionInput {
  email: string;
  liveUuid: string;
  live: LiveParticipantOption[];
  emailsByUuid: Map<string, string>;
  plan: AssignmentPlan;
  placedUuids: Set<string>;
  inFlightUuids: Set<string>;
  operatorUuids: Set<string>;
  manualDrafts: Map<string, ManualPlacementDraft>;
  attendeeKeyOf: (participant: RoomParticipant) => string;
}

function normalizeEmail(email: string | undefined): string {
  return (email ?? "").trim().toLowerCase();
}

/** Match only a unique Zoom-consented email on both sides, without overriding host work. */
export function resolveLateEmailMatch(input: LateEmailResolutionInput): LateEmailResolution {
  const email = normalizeEmail(input.email);
  if (!email) return { kind: "none", reason: "email_empty" };
  if (input.operatorUuids.has(input.liveUuid)) return { kind: "none", reason: "live_participant_is_operator" };

  const liveMatches = input.live.filter((candidate) => normalizeEmail(input.emailsByUuid.get(candidate.uuid)) === email);
  const planMatches = input.plan.rooms.flatMap((room) =>
    room.participants
      .filter((participant) => normalizeEmail(participant.email) === email)
      .map((participant) => ({
        live: liveMatches[0],
        participant,
        roomName: room.room_name,
        roomKey: room.room_key,
        attendeeKey: input.attendeeKeyOf(participant),
      })),
  );
  if (planMatches.length === 0) return { kind: "none", reason: "email_not_in_current_round" };
  const match = planMatches[0];
  if (liveMatches.length === 0) return { kind: "none", reason: "email_not_linked_to_live_roster" };
  if (liveMatches.length > 1) {
    return {
      kind: "conflict",
      match,
      message: "This Zoom email is associated with multiple live attendees. Review the identity manually.",
    };
  }
  if (liveMatches[0].uuid !== input.liveUuid) return { kind: "none", reason: "email_linked_to_different_live_uuid" };

  if (planMatches.length !== 1) {
    return {
      kind: "conflict",
      match,
      message: "This Zoom email matches multiple planned participants. Review the identity manually.",
    };
  }

  const placedParticipant = input.plan.rooms
    .flatMap((room) => room.participants)
    .find((participant) => participant.zoom_participant_id === input.liveUuid);
  if (placedParticipant) {
    return input.attendeeKeyOf(placedParticipant) === match.attendeeKey
      ? { kind: "already-assigned" }
      : {
          kind: "conflict",
          match,
          message: "The Zoom email identifies a different attendee than this existing placement. Review it manually.",
        };
  }
  if (input.placedUuids.has(input.liveUuid)) {
    return {
      kind: "conflict",
      match,
      message: "This attendee already has a room assignment. Review the Zoom email before changing it.",
    };
  }
  if (input.inFlightUuids.has(input.liveUuid)) return { kind: "already-assigned" };

  if (match.participant.zoom_participant_id) {
    return {
      kind: "conflict",
      match,
      message: "This planned participant is already assigned to another Zoom attendee.",
    };
  }

  const liveDraft = input.manualDrafts.get(input.liveUuid);
  if (liveDraft) {
    if (liveDraft.attendeeKey === match.attendeeKey && liveDraft.roomKey === match.roomKey) {
      return {
        kind: "confirmed",
        match,
        message: "Zoom email confirms this selection. Use Place selected to complete the placement.",
      };
    }
    return {
      kind: "conflict",
      match,
      message: "Zoom email points to a different participant than this draft. Review the selection.",
    };
  }

  const reservedByOther = Array.from(input.manualDrafts.entries()).some(
    ([uuid, draft]) => uuid !== input.liveUuid && draft.attendeeKey === match.attendeeKey,
  );
  if (reservedByOther) {
    return {
      kind: "conflict",
      match,
      message: "This planned participant is selected for another live attendee. Resolve the selections manually.",
    };
  }

  return { kind: "auto", match };
}
